import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { User, CreateUserRequest } from '../types'
import { getUsers, createUser, updateUser, deleteUser } from '../services/users'

interface UsersState {
  // State
  users: User[]
  loading: boolean
  error: string | null
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean

  // Actions
  fetchUsers: () => Promise<void>
  addUser: (userData: CreateUserRequest) => Promise<User>
  editUser: (id: string, userData: Partial<CreateUserRequest>) => Promise<User>
  removeUser: (id: string) => Promise<void>
  clearError: () => void
  setLoading: (loading: boolean) => void
}

export const useUsersStore = create<UsersState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        users: [],
        loading: false,
        error: null,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,

        fetchUsers: async () => {
          try {
            set({ loading: true, error: null })
            const users = await getUsers()

            set({
              users,
              loading: false,
            })
          } catch (error: any) {
            console.error('Error fetching users:', error)
            set({
              error: error.response.data.error || 'Failed to fetch users',
              loading: false,
            })
          }
        },

        addUser: async (userData: CreateUserRequest) => {
          try {
            set({ isCreating: true, error: null })
            const newUser = await createUser(userData)

            // Add the new user to the store
            set((state) => ({
              users: [...state.users, newUser],
              isCreating: false,
            }))

            return newUser
          } catch (error: any) {
            console.error('Error creating user:', error)
            set({
              error: error.response.data.error || 'Failed to create user',
              isCreating: false,
            })
            throw error
          }
        },

        editUser: async (id: string, userData: Partial<CreateUserRequest>) => {
          try {
            set({ isUpdating: true, error: null })
            const updatedUser = await updateUser(id, userData)

            // Update the user in the store
            set((state) => ({
              users: state.users.map((user) =>
                user._id === id ? updatedUser : user,
              ),
              isUpdating: false,
            }))

            return updatedUser
          } catch (error: any) {
            console.error('Error updating user:', error)
            set({
              error: error.response.data.error || 'Failed to update user',
              isUpdating: false,
            })
            throw error
          }
        },

        removeUser: async (id: string) => {
          try {
            set({ isDeleting: true, error: null })
            await deleteUser(id)

            // Remove the user from the store
            set((state) => ({
              users: state.users.filter((user) => user._id !== id),
              isDeleting: false,
            }))
          } catch (error: any) {
            console.error('Error deleting user:', error)
            set({
              error: error.response.data.error || 'Failed to delete user',
              isDeleting: false,
            })
            throw error
          }
        },

        clearError: () => {
          set({ error: null })
        },

        setLoading: (loading: boolean) => {
          set({ loading })
        },
      }),
      {
        name: 'users-store',
      },
    ),
  ),
)
